import React, { useEffect, useState } from 'react';
import CustomerWrapper from '../../../components/Customer/CustomerWrapper';
import { useGetCustomer, usePostCustomer, usePutCustomer, useSetCustomerState, useGetUsersEnum } from '../hooks/api/customerApi';
import PropTypes from 'prop-types';
import useAppContext from '../../../hooks/context/useAppContext';
import { useIntl } from 'react-intl';

const skeletUser = (users) => {
  const skelet = [];
  if (users) {
    for (let i = 0; i < users.length; i++) {
      if (users[i] && users[i] !== -1) {
        skelet.push({ id: users[i] });
      }
    }
  }
  return skelet;
};

const Customer = ({ id }) => {
  const { getCustomer, data, loading, error } = useGetCustomer();
  const { putCustomer, data: putData, loading: putLoading, error: putError } = usePutCustomer();
  const { postCustomer, data: postData, loading: postLoading, error: postError } = usePostCustomer();
  const { postCustomerState, data: postStateData, loading: postStateLoading, error: postStateError } = useSetCustomerState();
  const { getUsersEnum, data: usersData, loading: usersLoading, error: usersError } = useGetUsersEnum();

  const [errorObject, setErrorObject] = useState(error);
  const [loadingObject, setLoadingObject] = useState(loading);

  const [usersEnum, setUsersEnum] = useState([]);
  const [name, setName] = useState(null);
  const [idExternal, setIdExternal] = useState(null);
  const [organizationNumber, setOrganizationNumber] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [kamIds, setKamIds] = useState(undefined);
  const [customersIds, setCustomersIds] = useState([]);
  const [city, setCity] = useState(null);
  const [street, setStreet] = useState(null);
  const [zip, setZip] = useState(null);
  const [state, setState] = useState(null);
  const { setBreadCrumbs } = useAppContext();
  const intl = useIntl();

  const customerDetailTitle = intl.formatMessage({ id: 'app.customer.detail' });
  const customerNewTitle = intl.formatMessage({ id: 'app.customer.new' });

  useEffect(() => {
    if (id === 'new') {
      setBreadCrumbs(`${customerNewTitle}`);
    } else {
      setBreadCrumbs(`${customerDetailTitle}`);
    }
  }, [setBreadCrumbs, customerDetailTitle, customerNewTitle, id]);

  const usersObject = () => {
    const users = customersIds.concat(kamIds);
    return skeletUser(users);
  };

  const sendHandler = () => {
    const usersId = usersObject();
    const body = {
      idExternal: idExternal,
      name: name,
      nameSearch: apiResponse?.nameSearch || null,
      organizationNumber: organizationNumber,
      city: city,
      street: street,
      zip: zip,
      users: usersId,
    };
    if (id !== 'new') {
      Object.assign(body, { id: id });
      putCustomer(id, body);
    } else {
      postCustomer(body);
    }
  };

  const sendCustomerState = () => {
    const action = state && state === 1 ? 'deactivate' : 'activate';
    postCustomerState(id, action);
    setState(state === 1 ? 0 : 1);
  };

  const customer = {
    id,
    name,
    idExternal,
    organizationNumber,
    kamIds,
    customersIds,
    city,
    street,
    zip,
    state,
    putData,
    putLoading,
    postData,
    putError,
    postError,
    postLoading,
    postStateError,
    postStateLoading,
    postStateData,
    usersLoading,
    usersError,
    data,
    usersEnum,
  };

  const handlers = {
    setName,
    setIdExternal,
    setOrganizationNumber,
    sendHandler,
    sendCustomerState,
    setKamIds,
    setCustomersIds,
    setStreet,
    setZip,
    setCity,
    useState,
  };

  useEffect(() => {
    if (id !== 'new') {
      (async () => {
        await getCustomer(id);
      })();
    }
  }, [id, getCustomer]);

  useEffect(() => {
    (async () => {
      await getUsersEnum();
    })();
  }, [getUsersEnum]);

  useEffect(() => {
    if (usersData) {
      setUsersEnum(usersData);
    }
  }, [usersData]);

  useEffect(() => {
    setErrorObject(error);
    setLoadingObject(loading);
    if (data) {
      setCity(data.city);
      setStreet(data.street);
      setZip(data.zip);
      setApiResponse(data);
      setName(data.name);
      setIdExternal(data.idExternal);
      setOrganizationNumber(data.organizationNumber);
      setState(data.state);
      setKamIds(() => {
        return data.users.find((user) => user.role === 'KAM')?.id;
      });
      setCustomersIds(() => {
        return data.users
          .filter((user) => user.role === 'CUSTOMER' || user.role === 'CUSTOMER_VIEWER')
          .map((user) => {
            return user.id;
          });
      });
    }
  }, [data, loading, error]);

  return (
    <>
      <CustomerWrapper id={id} loading={loadingObject} error={errorObject} customer={customer} handlers={handlers} />
    </>
  );
};

Customer.propTypes = {
  id: PropTypes.string,
};
export default Customer;
